/**
 * Display rules for the ordered order types of a work order (contract v2 §2).
 *
 * The ordered `order_types` items are the only source of truth: plurality is
 * the number of items and is never derived from separators inside labels.
 */
type WorkOrderOrderTypeSource = {
  order_types?: readonly WorkOrderOrderTypeItem[] | null
}

export type OrderTypeHeadingVariant = "Auftragstyp" | "Auftragsart" | "Typ"

const PLURAL_HEADINGS: Record<OrderTypeHeadingVariant, string> = {
  Auftragstyp: "Auftragstypen",
  Auftragsart: "Auftragsarten",
  Typ: "Typen",
}

const clean = (value: unknown): string | null => {
  const text = `${value ?? ""}`.trim()

  return text || null
}

/** Ordered items that carry at least one usable label. */
export const orderTypeItems = (
  source: WorkOrderOrderTypeSource | null | undefined,
): WorkOrderOrderTypeItem[] =>
  (source?.order_types ?? []).filter((item) =>
    Boolean(clean(item?.internal_label) ?? clean(item?.public_label)),
  )

const internalItemLabel = (item: WorkOrderOrderTypeItem): string =>
  clean(item.internal_label) ?? clean(item.public_label) ?? ""

const publicItemLabel = (item: WorkOrderOrderTypeItem): string =>
  clean(item.public_label) ?? clean(item.internal_label) ?? ""

/** Ordered internal labels, one entry per order type. */
export const internalOrderTypeLabels = (
  source: WorkOrderOrderTypeSource | null | undefined,
): string[] => orderTypeItems(source).map(internalItemLabel)

/** Ordered public labels, one entry per order type. */
const publicOrderTypeLabels = (
  source: WorkOrderOrderTypeSource | null | undefined,
): string[] => orderTypeItems(source).map(publicItemLabel)

/** Natural German list: "A", "A und B", "A, B und C". */
export const joinGermanList = (labels: readonly string[]): string => {
  const parts = labels.map((label) => clean(label)).filter((label): label is string => !!label)

  if (parts.length <= 1) return parts[0] ?? ""

  return `${parts.slice(0, -1).join(", ")} und ${parts[parts.length - 1]}`
}

/** Admin display label: internal labels joined with ", ". */
export const internalOrderTypeLabel = (
  source: WorkOrderOrderTypeSource | null | undefined,
): string | null => clean(internalOrderTypeLabels(source).join(", "))

/** Customer-facing label as natural German list of the public labels. */
export const publicOrderTypeLabel = (
  source: WorkOrderOrderTypeSource | null | undefined,
): string | null => clean(joinGermanList(publicOrderTypeLabels(source)))

export const hasMultipleOrderTypes = (
  source: WorkOrderOrderTypeSource | null | undefined,
): boolean => orderTypeItems(source).length > 1

export const orderTypeHeadingLabel = (
  source: WorkOrderOrderTypeSource | null | undefined,
  variant: OrderTypeHeadingVariant = "Auftragstyp",
): string => (hasMultipleOrderTypes(source) ? PLURAL_HEADINGS[variant] : variant)

/** Free-text order type with identical internal and public label. */
export const buildCustomOrderType = (
  label: string | null | undefined,
): WorkOrderCustomOrderType | null => {
  const normalized = clean(label)

  if (!normalized) return null

  return {
    internal_label: normalized,
    public_label: normalized,
  }
}
